import type { PickList, PickItem, PerformanceMetrics, ExportOptions } from '../types';

export interface ExportData {
  pickLists?: PickList[];
  pickItems?: PickItem[];
  metrics?: PerformanceMetrics;
  metadata?: {
    exportedAt: string;
    exportedBy: string;
    filters?: any;
  };
}

export class ExportService {
  async exportToExcel(data: ExportData, options: Partial<ExportOptions> = {}): Promise<Blob> {
    // Create workbook structure
    const workbook = {
      SheetNames: [] as string[],
      Sheets: {} as any
    };

    // Add Pick Lists sheet
    if (data.pickLists) {
      const pickListsData = this.preparePickListsForExport(data.pickLists);
      workbook.Sheets['Pick Lists'] = this.arrayToSheet(pickListsData);
      workbook.SheetNames.push('Pick Lists');
    }

    // Add Pick Items sheet
    if (data.pickItems) {
      const pickItemsData = this.preparePickItemsForExport(data.pickItems);
      workbook.Sheets['Pick Items'] = this.arrayToSheet(pickItemsData);
      workbook.SheetNames.push('Pick Items');
    }

    // Add Metrics sheet
    if (data.metrics) {
      const metricsData = this.prepareMetricsForExport(data.metrics);
      workbook.Sheets['Metrics'] = this.arrayToSheet(metricsData);
      workbook.SheetNames.push('Metrics');
    }

    // Convert to Excel format (simplified implementation)
    const excelBuffer = this.workbookToBuffer(workbook);
    return new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  async exportToPDF(data: ExportData, options: Partial<ExportOptions> = {}): Promise<Blob> {
    // Create PDF content
    const pdfContent = this.generatePDFContent(data, options);
    
    // Convert to PDF (simplified implementation)
    const pdfBuffer = await this.htmlToPDF(pdfContent);
    return new Blob([pdfBuffer], { type: 'application/pdf' });
  }

  async exportToCSV(data: ExportData, options: Partial<ExportOptions> = {}): Promise<Blob> {
    let csvContent = '';

    if (data.pickLists) {
      csvContent += 'PICK LISTS\n';
      csvContent += this.arrayToCSV(this.preparePickListsForExport(data.pickLists));
      csvContent += '\n\n';
    }

    if (data.pickItems) {
      csvContent += 'PICK ITEMS\n';
      csvContent += this.arrayToCSV(this.preparePickItemsForExport(data.pickItems));
      csvContent += '\n\n';
    }

    if (data.metrics) {
      csvContent += 'METRICS\n';
      csvContent += this.arrayToCSV(this.prepareMetricsForExport(data.metrics));
    }

    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  }

  async exportToJSON(data: ExportData, options: Partial<ExportOptions> = {}): Promise<Blob> {
    const jsonData = {
      ...data,
      exportInfo: {
        exportedAt: new Date().toISOString(),
        format: 'json',
        options
      }
    };

    const jsonString = JSON.stringify(jsonData, null, 2);
    return new Blob([jsonString], { type: 'application/json' });
  }

  generateDeliveryManifest(pickLists: PickList[], deliveryInfo: any): Blob {
    const manifestContent = this.generateManifestHTML(pickLists, deliveryInfo);
    return new Blob([manifestContent], { type: 'text/html' });
  }

  generatePackingSlip(pickList: PickList, items: PickItem[]): Blob {
    const packingSlipContent = this.generatePackingSlipHTML(pickList, items);
    return new Blob([packingSlipContent], { type: 'text/html' });
  }

  private preparePickListsForExport(pickLists: PickList[]): any[] {
    return pickLists.map(list => ({
      'Order Number': list.orderNumber,
      'Status': list.status,
      'Customer': list.customerName,
      'Priority': list.priority,
      'Total Items': list.totalItems,
      'Picked Items': list.pickedItems,
      'Progress': `${Math.round((list.pickedItems / list.totalItems) * 100)}%`,
      'Estimated Time': `${list.estimatedTime} min`,
      'Actual Time': list.actualTime ? `${list.actualTime} min` : 'N/A',
      'Created': new Date(list.createdAt).toLocaleDateString(),
      'Due Date': list.dueDate ? new Date(list.dueDate).toLocaleDateString() : 'N/A',
      'Temperature Zone': list.temperatureZone || 'Ambient',
      'Picking Type': list.pickingType
    }));
  }

  private preparePickItemsForExport(pickItems: PickItem[]): any[] {
    return pickItems.map(item => ({
      'Pick List ID': item.pickListId,
      'Product SKU': item.productSku,
      'Product Name': item.productName,
      'Bin Location': item.binLocation,
      'Quantity Required': item.quantityRequired,
      'Quantity Picked': item.quantityPicked,
      'Status': item.status,
      'Temperature Zone': item.temperatureZone || 'Ambient',
      'Expiry Date': item.expiryDate || 'N/A',
      'Lot Number': item.lotNumber || 'N/A',
      'Notes': item.notes || '',
      'Created': new Date(item.createdAt).toLocaleDateString(),
      'Updated': new Date(item.updatedAt).toLocaleDateString()
    }));
  }

  private prepareMetricsForExport(metrics: PerformanceMetrics): any[] {
    return [
      { 'Metric': 'Total Picks Today', 'Value': metrics.totalPicksToday },
      { 'Metric': 'Average Pick Time (min)', 'Value': metrics.averagePickTime },
      { 'Metric': 'Accuracy (%)', 'Value': metrics.accuracy },
      { 'Metric': 'Efficiency (%)', 'Value': metrics.efficiency },
      { 'Metric': 'Completed Orders', 'Value': metrics.completedOrders },
      { 'Metric': 'Pending Orders', 'Value': metrics.pendingOrders },
      { 'Metric': 'Pick Rate (items/hour)', 'Value': metrics.pickRate || 0 },
      { 'Metric': 'Error Rate (%)', 'Value': metrics.errorRate || 0 },
      { 'Metric': 'On-Time Delivery (%)', 'Value': metrics.onTimeDelivery || 0 }
    ];
  }

  private arrayToSheet(data: any[]): any {
    if (data.length === 0) return {};

    const headers = Object.keys(data[0]);
    const sheetData = [headers, ...data.map(row => headers.map(header => row[header]))];
    
    const sheet: any = {};
    const range = { s: { c: 0, r: 0 }, e: { c: headers.length - 1, r: data.length } };
    
    for (let R = 0; R <= range.e.r; R++) {
      for (let C = 0; C <= range.e.c; C++) {
        const cellAddress = this.encodeCellAddress(C, R);
        const cellValue = sheetData[R][C];
        sheet[cellAddress] = { v: cellValue, t: typeof cellValue === 'number' ? 'n' : 's' };
      }
    }
    
    sheet['!ref'] = `A1:${this.encodeCellAddress(range.e.c, range.e.r)}`;
    return sheet;
  }

  private arrayToCSV(data: any[]): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ];

    return csvRows.join('\n');
  }

  private encodeCellAddress(col: number, row: number): string {
    let colStr = '';
    let c = col;
    while (c >= 0) {
      colStr = String.fromCharCode(65 + (c % 26)) + colStr;
      c = Math.floor(c / 26) - 1;
    }
    return colStr + (row + 1);
  }

  private workbookToBuffer(workbook: any): ArrayBuffer {
    // Simplified Excel file generation
    // In a real implementation, you would use a library like SheetJS
    const jsonString = JSON.stringify(workbook, null, 2);
    const buffer = new ArrayBuffer(jsonString.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < jsonString.length; i++) {
      view[i] = jsonString.charCodeAt(i);
    }
    return buffer;
  }

  private async htmlToPDF(html: string): Promise<ArrayBuffer> {
    // Simplified PDF generation
    // In a real implementation, you would use a library like jsPDF or Puppeteer
    const buffer = new ArrayBuffer(html.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < html.length; i++) {
      view[i] = html.charCodeAt(i);
    }
    return buffer;
  }

  private generatePDFContent(data: ExportData, options: Partial<ExportOptions>): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Warehouse Picking Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .header { text-align: center; margin-bottom: 30px; }
          .section { margin: 30px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Warehouse Picking Report</h1>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        
        ${data.pickLists ? this.generatePickListsTable(data.pickLists) : ''}
        ${data.pickItems ? this.generatePickItemsTable(data.pickItems) : ''}
        ${data.metrics ? this.generateMetricsTable(data.metrics) : ''}
      </body>
      </html>
    `;
  }

  private generatePickListsTable(pickLists: PickList[]): string {
    const data = this.preparePickListsForExport(pickLists);
    const headers = Object.keys(data[0] || {});
    
    return `
      <div class="section">
        <h2>Pick Lists</h2>
        <table>
          <thead>
            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${data.map(row => 
              `<tr>${headers.map(h => `<td>${row[h]}</td>`).join('')}</tr>`
            ).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private generatePickItemsTable(pickItems: PickItem[]): string {
    const data = this.preparePickItemsForExport(pickItems);
    const headers = Object.keys(data[0] || {});
    
    return `
      <div class="section">
        <h2>Pick Items</h2>
        <table>
          <thead>
            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${data.map(row => 
              `<tr>${headers.map(h => `<td>${row[h]}</td>`).join('')}</tr>`
            ).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private generateMetricsTable(metrics: PerformanceMetrics): string {
    const data = this.prepareMetricsForExport(metrics);
    
    return `
      <div class="section">
        <h2>Performance Metrics</h2>
        <table>
          <thead>
            <tr><th>Metric</th><th>Value</th></tr>
          </thead>
          <tbody>
            ${data.map(row => 
              `<tr><td>${row.Metric}</td><td>${row.Value}</td></tr>`
            ).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private generateManifestHTML(pickLists: PickList[], deliveryInfo: any): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Delivery Manifest</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .delivery-info { margin: 20px 0; }
          .orders { margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>DELIVERY MANIFEST</h1>
          <p>Date: ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="delivery-info">
          <h3>Delivery Information</h3>
          <p><strong>Driver:</strong> ${deliveryInfo.driver || 'TBD'}</p>
          <p><strong>Vehicle:</strong> ${deliveryInfo.vehicle || 'TBD'}</p>
          <p><strong>Route:</strong> ${deliveryInfo.route || 'TBD'}</p>
        </div>
        
        <div class="orders">
          <h3>Orders (${pickLists.length})</h3>
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Priority</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pickLists.map(list => `
                <tr>
                  <td>${list.orderNumber}</td>
                  <td>${list.customerName}</td>
                  <td>${list.totalItems}</td>
                  <td>${list.priority}</td>
                  <td>${list.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </body>
      </html>
    `;
  }

  private generatePackingSlipHTML(pickList: PickList, items: PickItem[]): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${pickList.orderNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .order-info { margin: 20px 0; }
          .items { margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .footer { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PACKING SLIP</h1>
          <h2>${pickList.orderNumber}</h2>
        </div>
        
        <div class="order-info">
          <p><strong>Customer:</strong> ${pickList.customerName}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>Priority:</strong> ${pickList.priority}</p>
        </div>
        
        <div class="items">
          <table>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.productSku}</td>
                  <td>${item.productName}</td>
                  <td>${item.quantityPicked}</td>
                  <td>${item.binLocation}</td>
                  <td>${item.expiryDate || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="footer">
          <p><strong>Total Items:</strong> ${items.length}</p>
          <p><strong>Packed By:</strong> _________________</p>
          <p><strong>Date/Time:</strong> _________________</p>
        </div>
      </body>
      </html>
    `;
  }
}

export const exportService = new ExportService();